import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Layers, DollarSign, MapPin, Zap } from "lucide-react";

const TIER_COLORS = {
  metro: "bg-purple-100 text-purple-700 border-purple-200",
  large: "bg-blue-100 text-blue-700 border-blue-200",
  medium: "bg-emerald-100 text-emerald-700 border-emerald-200",
  small: "bg-amber-100 text-amber-700 border-amber-200",
  micro: "bg-slate-100 text-slate-600 border-slate-200",
};

const TIER_LABELS = {
  metro: "Metro (1000+)",
  large: "Large (500-999)",
  medium: "Medium (100-499)",
  small: "Small (20-99)",
  micro: "Micro (<20)",
};

export default function BatchBudgetPanel() {
  const [tiers, setTiers] = useState([]);
  const [summary, setSummary] = useState({});
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classifying, setClassifying] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [tierRes, budgetRes] = await Promise.all([
        axios.get(`${API}/admin/city-tiers`),
        axios.get(`${API}/admin/nightly-budget`),
      ]);
      setTiers(tierRes.data.tiers || []);
      setSummary(tierRes.data.summary || {});
      setBudget(budgetRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const classifyCity = async (city, state = "CA") => {
    setClassifying(city);
    try {
      const res = await axios.post(`${API}/admin/city-tiers/classify/${encodeURIComponent(city)}?state=${state}`);
      toast.success(`${city}: ${res.data.business_tier} (${res.data.business_count} businesses)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Classification failed");
    } finally { setClassifying(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const budgetPlan = budget?.plan || [];
  const tierBreakdown = budget?.tier_breakdown || {};
  const totalAllocated = budget?.total_foursquare_allocated || 0;
  const ceiling = budget?.foursquare_ceiling || 950;

  return (
    <div className="space-y-4" data-testid="batch-budget-panel">
      {/* Tier summary stats */}
      <div className="grid grid-cols-5 gap-1.5">
        {["metro", "large", "medium", "small", "micro"].map(t => (
          <div key={t} className={`rounded-lg p-2 text-center border ${TIER_COLORS[t]}`}>
            <p className="text-lg font-bold" data-testid={`tier-count-${t}`}>{summary[t] || 0}</p>
            <p className="text-[9px] font-medium capitalize">{t}</p>
          </div>
        ))}
      </div>

      {/* Foursquare budget gauge */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="foursquare-budget">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold text-slate-600 flex items-center gap-1">
            <Zap className="w-3.5 h-3.5" /> Foursquare Budget
          </span>
          <span className="text-[10px] text-slate-400">{totalAllocated} / {ceiling} calls</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div
            className="bg-sky-500 h-2 rounded-full transition-all"
            style={{ width: `${Math.min((totalAllocated / ceiling) * 100, 100)}%` }}
          />
        </div>
        {Object.keys(tierBreakdown).length > 0 && (
          <div className="flex gap-2 mt-2 flex-wrap">
            {["metro", "large", "medium", "small", "micro"].map(t => {
              const info = tierBreakdown[t];
              if (!info) return null;
              return (
                <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded border ${TIER_COLORS[t]}`}>
                  {t}: {info.cities}c / {info.foursquare_calls} calls
                </span>
              );
            })}
          </div>
        )}
        {budgetPlan.length === 0 && (
          <p className="text-[10px] text-slate-400 mt-1">No nightly run with budget allocation yet</p>
        )}
      </div>

      {/* City tier list */}
      {tiers.length > 0 && (
        <div data-testid="tier-list">
          <p className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> Classified Cities ({tiers.length})
          </p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {tiers.slice(0, 30).map((t) => (
              <div key={`${t.city}-${t.state}`}
                   className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-lg"
                   data-testid={`tier-city-${t.city}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-slate-800">{t.city}, {t.state}</span>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                    <span>{t.business_count} businesses</span>
                    <span>{t.business_tier_method}</span>
                  </div>
                </div>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold border ${TIER_COLORS[t.business_tier] || TIER_COLORS.micro}`}>
                  {t.business_tier}
                </span>
                <button onClick={() => classifyCity(t.city, t.state)} disabled={classifying === t.city}
                  className="p-1 text-slate-400 hover:text-slate-600 transition"
                  data-testid={`reclassify-${t.city}`}>
                  {classifying === t.city ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
